import * as React from "react";

import { useMeasure, type OnDimensionsChange } from "@/shared/hooks";
import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";
import { ErrorMonitoringService } from "@/shared/services";
import { LayoutItem, MapPanelState } from "@/shared/state/visualization";

import { type MapPanelSettingsSidebarProps } from "../../PanelSettings/MapPanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import {
  useEphemeralPanelState,
  useSelectPanel,
} from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import { ActivePointIndicator } from "./ActivePointIndicator";
import { CoordinateValidationError } from "./coordinateValidation";
import { TimestampedLocation } from "./geoTypes";
import { Legend } from "./Legend";
import { type MapLoadingState } from "./MapDataManager";
import { MapRenderer } from "./MapRenderer";

interface MapPanelProps {
  layout: LayoutItem;
  state: MapPanelState;
}

export function MapPanel({ layout, state }: MapPanelProps) {
  const timer = useWorkspaceTimer();
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();
  const [, updateEphemeralState] = useEphemeralPanelState();

  const mapContainer = React.useRef<HTMLDivElement | null>(null);
  const [mapRenderer, setMapRenderer] = React.useState<MapRenderer | null>(
    null,
  );
  const [error, setError] = React.useState<Error | null>(null);
  const [loadingState, setLoadingState] =
    React.useState<MapLoadingState>("complete");
  const [singleActiveLocation, setSingleActiveLocation] =
    React.useState<TimestampedLocation | null>(null);

  // Callback to write viewport changes to ephemeral state
  const onViewportChange = React.useCallback(
    (viewport: {
      center: { lng: number; lat: number };
      zoom: number;
      bearing: number;
      pitch: number;
    }) => {
      updateEphemeralState(state.id, { viewport });
    },
    [updateEphemeralState, state.id],
  );

  React.useEffect(() => {
    const container = mapContainer.current;

    if (container === null || error !== null) {
      return;
    }

    const abortController = new AbortController();

    const _mapRenderer = new MapRenderer({
      abortController: abortController,
      mapContainer: container,
      onError: (err: Error) => {
        const isValidationError = CoordinateValidationError.isError(err);

        setError(err);

        // Only log unexpected errors
        if (!isValidationError) {
          ErrorMonitoringService.captureError(err);
        }
      },
      setLoadingState: setLoadingState,
      timer: timer,
      setSingleActiveLocation: setSingleActiveLocation,
      onViewportChange: onViewportChange,
      initialViewport: state.initParams?.viewport,
    });

    _mapRenderer
      .initialize()
      .then(() => {
        if (abortController.signal.aborted) {
          return;
        }
        setMapRenderer(_mapRenderer);
      })
      .catch((err: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to initialize MapPanel", { cause: err });
        setError(error);
        ErrorMonitoringService.captureError(error);
      });

    return () => {
      // Abort after disposing to ensure all clean-up actions are complete
      _mapRenderer.dispose();
      abortController.abort();
    };
  }, [
    error,
    mapContainer,
    timer,
    onViewportChange,
    state.initParams?.viewport,
  ]);

  /**
   * Update the MapRenderer's reference to state as it changes
   */
  React.useEffect(
    function updateState() {
      if (mapRenderer === null) {
        return;
      }

      const abortController = new AbortController();
      mapRenderer.setState(state.data, abortController.signal);

      return function abort() {
        abortController.abort();
      };
    },
    [mapRenderer, state.data],
  );

  /**
   * Redraw the image when the div is resized
   */
  const onRenderingSurfaceResize = React.useCallback(
    function onResize() {
      if (mapRenderer === null) {
        return;
      }
      mapRenderer?.resize();
    },
    [mapRenderer],
  );

  const onRenderingSurfaceResizeDebounced =
    useDebouncedCallback<OnDimensionsChange>(onRenderingSurfaceResize, 100);

  const [measured] = useMeasure<HTMLDivElement>({
    onDimensionsChange: onRenderingSurfaceResizeDebounced,
  });

  // Error takes precedence over the empty-state message so the empty-state
  // Alert can't stack on top of RenderingError and hide its Reload button.
  // The error persists until the user clicks Reload; nothing else clears it.
  let statusOverlay: React.ReactNode = null;
  if (error !== null) {
    statusOverlay = (
      <RenderingError
        error={error}
        title={
          CoordinateValidationError.isError(error)
            ? "Invalid coordinate data"
            : undefined
        }
        onClearError={() => setError(null)}
      />
    );
  } else if (state.data.length === 0) {
    statusOverlay = (
      <NoDataMessage
        panelData={state.data}
        onAddDataAction={() => {
          selectPanel(state.id);
          sidebar.openView<MapPanelSettingsSidebarProps>({
            type: SidebarViewType.PanelSettings,
            data: {
              field: "Paths",
              spotlightKey: performance.now(),
            },
          });
        }}
      />
    );
  }

  return (
    <PanelLayout
      isLoading={loadingState === "loading-initial"}
      state={state}
      layout={layout}
      header={
        <PanelHeader
          state={state}
          backgroundLoading={loadingState === "loading-remaining"}
        />
      }
    >
      {state.data.length === 1 && singleActiveLocation && (
        // Show active point indicator if there's only one path on the map
        <ActivePointIndicator activeLocation={singleActiveLocation} />
      )}

      {state.data.length > 1 && (
        // Show legend if there are multiple paths
        <Legend
          state={state}
          fitCameraToPath={(pathId) => mapRenderer?.fitCameraToPath(pathId)}
        />
      )}

      <div
        id="map"
        ref={(node) => {
          if (node !== null) {
            measured(node);
          }
          mapContainer.current = node;
        }}
        style={{ width: "100%", height: "100%" }}
      />
      {statusOverlay}
      <DataNotFoundMessage panelId={state.id} />
    </PanelLayout>
  );
}
